import React, { lazy, Suspense } from "react";
import { Navigate, Routes, Route, useLocation, useParams } from "react-router-dom";
import Navbar from "./components/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import FloatingScheduleCallButton from "./components/FloatingScheduleCallButton";
import NonProductionSeoGuard from "./components/NonProductionSeoGuard";
import { isProductionHost } from "./utils/isProductionHost";

// Only one navbar/footer variant is shown per page, so load the others on demand.
const NavbarFr = lazy(() => import("./components/NavbarFr"));
const LocationNavbar = lazy(() => import("./components/LocationNavbar"));
const Footer = lazy(() => import("./components/Footer"));
const FooterFr = lazy(() => import("./components/FooterFr"));
const LocationFooter = lazy(() => import("./components/LocationFooter.jsx"));

const AmanAhuja = lazy(() => import("./pages/author/AmanAhuja.jsx"));

// Providers
import { ReviewsProvider } from "./context/ReviewsContext";
import { BlogProvider } from "./context/BlogContext";

// 404
const NotFound = lazy(() => import("./pages/404/NotFound.jsx"));

// Thank You
const BookAppointmentThankYou = lazy(() => import("./pages/Thank You/BookAppointmentThankYou.jsx"));

// Pages
import Home from "./pages/Home";

// Life
const EverythingWeSell = lazy(() => import("./pages/life/EverythingWeSell.jsx"));
const LifeInsurance = lazy(() => import("./pages/life/LifeInsurance.jsx"));
const LifeInsuranceFrench = lazy(() => import("./pages/life/LifeInsuranceFrench.jsx"));
const LifeInsuranceBrampton = lazy(() => import("./pages/life/LifeInsuranceBrampton.jsx"));
const LifeInsuranceCalgary = lazy(() => import("./pages/life/LifeInsuranceCalgary.jsx"));
const LifeInsuranceMississauga = lazy(() => import("./pages/life/LifeInsuranceMississauga.jsx"));
const LifeInsuranceWinnipeg = lazy(() => import("./pages/life/LifeInsuranceWinnipeg.jsx"));
const LifeInsuranceToronto = lazy(() => import("./pages/life/LifeInsuranceToronto.jsx"));
const LifeInsuranceEbook = lazy(() => import("./pages/life/LifeInsuranceEbook.jsx"));
const LifeInsuranceEbookThankYou = lazy(() => import("./pages/life/LifeInsuranceEbookThankYou.jsx"));
const TermLifeInsurance = lazy(() => import("./pages/life/TermLifeInsurance.jsx"));
const TermLifeInsuranceFrench = lazy(() => import("./pages/life/TermLifeInsuranceFrench.jsx"));
const PermanentLifeInsurance = lazy(() => import("./pages/life/PermanentLifeInsurance.jsx"));
const MortgageInsurance = lazy(() => import("./pages/life/MortgageInsurance.jsx"));
const WholeLifeInsurance = lazy(() => import("./pages/life/WholeLifeInsurance.jsx"));
const UniversalLifeInsurance = lazy(() => import("./pages/life/UniversalLifeInsurance.jsx"));
const ParticipatingPermanentLifeInsurance = lazy(() => import("./pages/life/ParticipatingPermanentLifeInsurance.jsx"));
const NoMedicalInsurance = lazy(() => import("./pages/life/NoMedicalInsurance.jsx"));
const ChildrenInsurance = lazy(() => import("./pages/life/ChildrenInsurance.jsx"));
const HealthInsurance = lazy(() => import("./pages/life/HealthInsurance.jsx"));

// Travel
const VisitorToCanadaInsurance = lazy(() => import("./pages/travel/VisitorToCanadaInsurance.jsx"));
const SuperVisaInsurance = lazy(() => import("./pages/travel/SuperVisaInsurance.jsx"));
const SuperVisaInsuranceBrampton = lazy(() => import("./pages/travel/SuperVisaInsuranceBrampton.jsx"));
const NewcomersInsurance = lazy(() => import("./pages/travel/NewcomersInsurance.jsx"));
const InternationalStudentInsurance = lazy(() => import("./pages/travel/InternationalStudentInsurance.jsx"));
const TravelInsurance = lazy(() => import("./pages/travel/TravelInsurance.jsx"));

// Living benefits
const CriticalIllnessInsurance = lazy(() => import("./pages/living/CriticalIllnessInsurance.jsx"));
const DisabilityInsurance = lazy(() => import("./pages/living/DisabilityInsurance.jsx"));

// Investments
const FirstHomeSavingsAccount = lazy(() => import("./pages/investments/FirstHomeSavingsAccount"));
const RegisteredEducationSavingsPlan = lazy(() => import("./pages/investments/RegisteredEducationSavingsPlan"));
const TaxFreeSavingsAccount = lazy(() => import("./pages/investments/TaxFreeSavingsAccount"));
const RegisteredRetirementSavingsPlan = lazy(() => import("./pages/investments/RegisteredRetirementSavingsPlan"));
const RegisteredDisabilitySavingsPlan = lazy(() => import("./pages/investments/RegisteredDisabilitySavingsPlan"));
const EinsuredRdspCalculator = lazy(() => import("./pages/investments/EinsuredRdspCalculator.jsx"));

// Resources
const Blog = lazy(() => import("./pages/resources/Blog.jsx"));
const BlogPost = lazy(() => import("./pages/resources/BlogPost.jsx"));
const Faqs = lazy(() => import("./pages/resources/Faqs.jsx"));
const Reviews = lazy(() => import("./pages/resources/Reviews.jsx"));
const CaseStudies = lazy(() => import("./pages/resources/CaseStudies.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/resources/PrivacyPolicy.jsx"));
const TermsConditions = lazy(() => import("./pages/resources/TermsConditions.jsx"));
const Disclaimer = lazy(() => import("./pages/resources/Disclaimer"));
const AboutUs = lazy(() => import("./pages/resources/AboutUs.jsx"));

// Contact / Team
const ContactUs = lazy(() => import("./pages/contact/ContactUs.jsx"));
const OurTeam = lazy(() => import("./pages/contact/OurTeam.jsx"));
const Aman = lazy(() => import("./pages/contact/Aman.jsx"));
const Sunaina = lazy(() => import("./pages/contact/Sunaina.jsx"));
const Shikha = lazy(() => import("./pages/contact/Shikha.jsx"));
const Gurkiran = lazy(() => import("./pages/contact/Gurkiran.jsx"));
const Yash = lazy(() => import("./pages/contact/Yash.jsx"));
const Ekta = lazy(() => import("./pages/contact/Ekta.jsx"));
const Samridhi = lazy(() => import("./pages/contact/Samridhi.jsx"));
const Aarti = lazy(() => import("./pages/contact/Aarti.jsx"));

// Get Quote pages
const VisitorsInsuranceQuote = lazy(() => import("./pages/quotes/VisitorsInsuranceQuote.jsx"));
const VisitorsInsuranceQuoteFinal = lazy(() => import("./pages/quotes1/VisitorsInsuranceQuoteFinal.jsx"));
const ComparePlansPage = lazy(() => import("./pages/quotes1/ComparePlansPage.jsx"));
const GetStarted = lazy(() => import("./pages/GetStarted.jsx"));
const CompanyComparisonTool = lazy(() => import("./pages/quotes/CompanyComparisonTool.jsx"));
const CompareVtcPage = lazy(() => import("./pages/quotes1/CompareVtcPage.jsx"));

// Sign In page
const SignIn = lazy(() => import("./pages/auth/SignIn.jsx"));

const ApplyNowVisitorsInsurance = lazy(() => import("./pages/email-form/ApplyNowVisitorsInsurance.jsx"));
const ApplyNowVisitorsInsuranceFinal = lazy(() => import("./pages/quotes1/ApplyNowVisitorsInsuranceFinal.jsx"));
const ApplyNowLifeInsurance = lazy(() => import("./pages/email-form/ApplyNowLifeInsurance.jsx"));
const LifeIntake = lazy(() => import("./pages/life/LifeIntake.jsx"));
const LifeIntakeAdmin = lazy(() => import("./pages/admin/LifeIntakeAdmin.jsx"));
const GiftCardMeet = lazy(() => import("./pages/GiftCardMeet.jsx"));

// SEO Meta
import { Helmet } from "react-helmet-async";

// ── French pages (/fr/*) ──────────────────────────────────────────────────────
const HomeFr = lazy(() => import("./pages/fr/Home"));
const GetStartedFr = lazy(() => import("./pages/fr/GetStarted"));

// Life (FR)
const EverythingWeSellFr = lazy(() => import("./pages/fr/life/EverythingWeSell"));
const LifeInsuranceFr = lazy(() => import("./pages/fr/life/LifeInsurance"));
const LifeInsuranceBramptonFr = lazy(() => import("./pages/fr/life/LifeInsuranceBrampton"));
const LifeInsuranceTorontoFr = lazy(() => import("./pages/fr/life/LifeInsuranceToronto"));
const LifeInsuranceCalgaryFr = lazy(() => import("./pages/fr/life/LifeInsuranceCalgary"));
const LifeInsuranceMississaugaFr = lazy(() => import("./pages/fr/life/LifeInsuranceMississauga"));
const LifeInsuranceWinnipegFr = lazy(() => import("./pages/fr/life/LifeInsuranceWinnipeg"));
const LifeInsuranceEbookFr = lazy(() => import("./pages/fr/life/LifeInsuranceEbook"));
const LifeInsuranceEbookThankYouFr = lazy(() => import("./pages/fr/life/LifeInsuranceEbookThankYou"));
const TermLifeInsuranceFr = lazy(() => import("./pages/fr/life/TermLifeInsurance"));
const PermanentLifeInsuranceFr = lazy(() => import("./pages/fr/life/PermanentLifeInsurance"));
const MortgageInsuranceFr = lazy(() => import("./pages/fr/life/MortgageInsurance"));
const WholeLifeInsuranceFr = lazy(() => import("./pages/fr/life/WholeLifeInsurance"));
const UniversalLifeInsuranceFr = lazy(() => import("./pages/fr/life/UniversalLifeInsurance"));
const ParticipatingPermanentLifeInsuranceFr = lazy(() => import("./pages/fr/life/ParticipatingPermanentLifeInsurance"));
const NoMedicalInsuranceFr = lazy(() => import("./pages/fr/life/NoMedicalInsurance"));
const ChildrenInsuranceFr = lazy(() => import("./pages/fr/life/ChildrenInsurance"));
const HealthInsuranceFr = lazy(() => import("./pages/fr/life/HealthInsurance"));

// Travel (FR)
const VisitorToCanadaInsuranceFr = lazy(() => import("./pages/fr/travel/VisitorToCanadaInsurance"));
const SuperVisaInsuranceFr = lazy(() => import("./pages/fr/travel/SuperVisaInsurance"));
const NewcomersInsuranceFr = lazy(() => import("./pages/fr/travel/NewcomersInsurance"));
const InternationalStudentInsuranceFr = lazy(() => import("./pages/fr/travel/InternationalStudentInsurance"));
const TravelInsuranceFr = lazy(() => import("./pages/fr/travel/TravelInsurance"));

// Living benefits (FR)
const CriticalIllnessInsuranceFr = lazy(() => import("./pages/fr/living/CriticalIllnessInsurance"));
const DisabilityInsuranceFr = lazy(() => import("./pages/fr/living/DisabilityInsurance"));

// Investments (FR)
const FirstHomeSavingsAccountFr = lazy(() => import("./pages/fr/investments/FirstHomeSavingsAccount"));
const RegisteredEducationSavingsPlanFr = lazy(() => import("./pages/fr/investments/RegisteredEducationSavingsPlan"));
const TaxFreeSavingsAccountFr = lazy(() => import("./pages/fr/investments/TaxFreeSavingsAccount"));
const RegisteredRetirementSavingsPlanFr = lazy(() => import("./pages/fr/investments/RegisteredRetirementSavingsPlan"));
const RegisteredDisabilitySavingsPlanFr = lazy(() => import("./pages/fr/investments/RegisteredDisabilitySavingsPlan"));
const EinsuredRdspCalculatorFr = lazy(() => import("./pages/fr/investments/EinsuredRdspCalculator"));

// Resources (FR)
const FaqsFr = lazy(() => import("./pages/fr/resources/Faqs"));
const ReviewsFr = lazy(() => import("./pages/fr/resources/Reviews"));
const CaseStudiesFr = lazy(() => import("./pages/fr/resources/CaseStudies"));
const PrivacyPolicyFr = lazy(() => import("./pages/fr/resources/PrivacyPolicy"));
const TermsConditionsFr = lazy(() => import("./pages/fr/resources/TermsConditions"));
const DisclaimerFr = lazy(() => import("./pages/fr/resources/Disclaimer"));
const AboutUsFr = lazy(() => import("./pages/fr/resources/AboutUs"));

// Contact / Team (FR)
const ContactUsFr = lazy(() => import("./pages/fr/contact/ContactUs"));
const OurTeamFr = lazy(() => import("./pages/fr/contact/OurTeam"));
const AmanFr = lazy(() => import("./pages/fr/contact/Aman"));
const SunainaFr = lazy(() => import("./pages/fr/contact/Sunaina"));
const ShikhaFr = lazy(() => import("./pages/fr/contact/Shikha"));
const GurkiranFr = lazy(() => import("./pages/fr/contact/Gurkiran"));
const YashFr = lazy(() => import("./pages/fr/contact/Yash"));
const EktaFr = lazy(() => import("./pages/fr/contact/Ekta"));
const SamridhiFr = lazy(() => import("./pages/fr/contact/Samridhi"));
const AartiFr = lazy(() => import("./pages/fr/contact/Aarti"));

// Quotes / Apply (FR)
const VisitorsInsuranceQuoteFinalFr = lazy(() => import("./pages/fr/quotes1/VisitorsInsuranceQuoteFinal"));
const CompareVtcPageFr = lazy(() => import("./pages/fr/quotes1/CompareVtcPage"));
const ComparePlansPageFr = lazy(() => import("./pages/fr/quotes1/ComparePlansPage"));
const ApplyNowVisitorsInsuranceFinalFr = lazy(() => import("./pages/fr/quotes1/ApplyNowVisitorsInsuranceFinal"));
const ApplyNowLifeInsuranceFr = lazy(() => import("./pages/fr/email-form/ApplyNowLifeInsurance"));

// Thank You (FR)
const BookAppointmentThankYouFr = lazy(() => import("./pages/fr/Thank You/BookAppointmentThankYou"));
// ─────────────────────────────────────────────────────────────────────────────

// Blog only exists in English — redirect old /fr/blogue/:name links to their
// English equivalent instead of 404ing.
function BlogueToBlogRedirect() {
  const { name } = useParams();
  return <Navigate to={`/blog/${name}`} replace />;
}

function App() {
  const location = useLocation();
  const isFrench = location.pathname === "/fr" || location.pathname.startsWith("/fr/");

  const noFooterGapRoutes = ["/book-appointment/thank-you", "/fr/rendez-vous/merci"];

  const removeFooterGap = noFooterGapRoutes.some(
    (p) => location.pathname === p || location.pathname === p + "/",
  );

  const noLayoutRoutes = [
    "/sign-in", "/apply/vtc", "/apply/life", "/intake/life", "/admin/life-intake",
    "/fr/demande/vtc", "/fr/demande/vie",
    "/gift-card-meet",
  ];

  const locationPagesRoutes = [
  "/life-insurance-brampton",
  "/life-insurance-calgary",
  "/life-insurance-mississauga",
  "/life-insurance-winnipeg",
  "/life-insurance-toronto",
  "/super-visa-insurance-brampton",
];

  const showLayout = !noLayoutRoutes.some(
    (route) => location.pathname === route || location.pathname === route + "/",
  );

  const isLocationPage = locationPagesRoutes.some(
    (route) => location.pathname === route || location.pathname === route + "/",
  );

  // Routes that should not be indexed by Google
  const noIndexRoutes = [
    "/sign-in",
    "/apply/vtc",
    "/apply/life",
    "/intake/life",
    "/admin/life-intake",
    "/book-appointment/thank-you",
    "/life-insurance/ebook/thank-you",
    "/fr/rendez-vous/merci",
    "/fr/guide-assurance-vie/merci",
  ];

  // Staging/preview hosts (staging.einsured.ca, squaretrigger preview,
  // etc.) run this exact same build with no other environment
  // differentiation, so without this they'd be fully indexable. Force
  // noindex site-wide anywhere that isn't the real production host.
  const shouldNoIndex =
    !isProductionHost() ||
    noIndexRoutes.some(
      (p) => location.pathname === p || location.pathname === p + "/",
    );

  return (
    <>
      {/* Global Robots tag */}
      <Helmet>
        <meta
          name="robots"
          content={shouldNoIndex ? "noindex, nofollow" : "index, follow"}
        />
      </Helmet>
      <NonProductionSeoGuard />

      <ReviewsProvider>
        <BlogProvider>
          <ScrollToTop />

          <Suspense fallback={null}>
            {showLayout && (isLocationPage ? <LocationNavbar /> : isFrench ? <NavbarFr /> : <Navbar />)}
          </Suspense>

          <main
            className={
              showLayout
                ? isLocationPage
                  ? "pt-[90px] md:pt-[100px]"
                  : "pt-[104px] md:pt-[112px]"
                : ""
            }
          >
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
<Routes>
              {/* Home */}
              <Route path="/" element={<Home />} />
              <Route
                path="/everything-we-sell"
                element={<EverythingWeSell />}
              />

              {/* Get Started page */}
              <Route path="/book-a-meeting" element={<GetStarted />} />
              <Route
                path="/get-started"
                element={<Navigate to="/book-a-meeting" replace />}
              />

              {/* Thank You page */}
              <Route
                path="/book-appointment/thank-you"
                element={<BookAppointmentThankYou />}
              />

              {/* Life */}
              <Route path="/life-insurance" element={<LifeInsurance />} />
              {/*
              <Route
                path="/fr/assurance-vie"
                element={<LifeInsuranceFrench />}
              />
              */}
              <Route
                path="/life-insurance-brampton"
                element={<LifeInsuranceBrampton />}
              />
               <Route
                path="/life-insurance-calgary"
                element={<LifeInsuranceCalgary />}
              />
              <Route
                path="/life-insurance-mississauga"
                element={<LifeInsuranceMississauga />}
              />
              <Route
                path="/life-insurance-winnipeg"
                element={<LifeInsuranceWinnipeg />}
              />
              <Route
                path="/life-insurance-toronto"
                element={<LifeInsuranceToronto />}
              />
              <Route
                path="/life-insurance/ebook"
                element={<LifeInsuranceEbook />}
              />
              <Route
                path="/life-insurance/ebook/thank-you"
                element={<LifeInsuranceEbookThankYou />}
              />
              <Route
                path="/term-life-insurance"
                element={<TermLifeInsurance />}
              />
              {/*
                <Route
                  path="/fr/assurance-vie-temporaire"
                  element={<TermLifeInsuranceFrench />}
                />
              */}

              <Route
                path="/permanent-life-insurance"
                element={<PermanentLifeInsurance />}
              />
              <Route
                path="/mortgage-insurance"
                element={<MortgageInsurance />}
              />
              <Route
                path="/whole-life-insurance"
                element={<WholeLifeInsurance />}
              />
              <Route
                path="/universal-life-insurance"
                element={<UniversalLifeInsurance />}
              />
              <Route
                path="/participating-permanent-life-insurance"
                element={<ParticipatingPermanentLifeInsurance />}
              />

              <Route
                path="/no-medical-insurance-in-canada"
                element={<Navigate to="/no-medical-insurance" replace />}
              />
              <Route
                path="/no-medical-insurance"
                element={<NoMedicalInsurance />}
              />
              <Route
                path="/children-insurance"
                element={<ChildrenInsurance />}
              />
              <Route path="/health-insurance" element={<HealthInsurance />} />

              {/* Travel */}
              <Route
                path="/visitor-to-canada-insurance"
                element={<VisitorToCanadaInsurance />}
              />
            <Route
  path="/super-visa-insurance"
  element={<SuperVisaInsurance />}
/>

<Route
  path="/super-visa-insurance-brampton"
  element={<SuperVisaInsuranceBrampton />}
/>

<Route
  path="/newcomers-insurance"
  element={<NewcomersInsurance />}
/>
              <Route
                path="/international-student-insurance"
                element={<InternationalStudentInsurance />}
              />
              <Route path="/travel-insurance" element={<TravelInsurance />} />

              {/* Living benefits */}
              <Route
                path="/critical-illness-insurance"
                element={<CriticalIllnessInsurance />}
              />
              <Route
                path="/disability-insurance"
                element={<DisabilityInsurance />}
              />

              {/* Investments */}
              <Route
                path="/first-home-savings-account"
                element={<FirstHomeSavingsAccount />}
              />
              <Route
                path="/registered-education-savings-plan"
                element={<RegisteredEducationSavingsPlan />}
              />
              <Route
                path="/tax-free-savings-account"
                element={<TaxFreeSavingsAccount />}
              />
              <Route
                path="/registered-retirement-savings-plan"
                element={<RegisteredRetirementSavingsPlan />}
              />
              <Route
                path="/registered-disability-savings-plan"
                element={<RegisteredDisabilitySavingsPlan />}
              />
              <Route
                path="/einsured-rdsp-calculator"
                element={<EinsuredRdspCalculator />}
              />

              {/* Resources */}
              <Route path="/blog" element={<Blog />} />

              {/* Blog archive / old WordPress style routes */}
              <Route
                path="/blog/author/hujaman"
                element={<Navigate to="/blog" replace />}
              />
              <Route
                path="/blog/category/blog"
                element={<Navigate to="/blog" replace />}
              />

              {/* Missing blog redirects - GSC Soft 404 fixes */}
              <Route
                path="/blog/super-visa-insurance-for-parents-and-grandparents"
                element={
                  <Navigate
                    to="/blog/super-visa-emergency-medical-insurance-the-key-to-a-worry-free-visit-for-your-parents-and-grandparents"
                    replace
                  />
                }
              />
              <Route
                path="/blog/affordable-super-visa-insurance-without-compromising-coverage"
                element={
                  <Navigate
                    to="/blog/affordable-insurance-solutions-for-super-visa-applicants-in-canada"
                    replace
                  />
                }
              />
              <Route
                path="/blog/how-to-use-life-insurance-as-part-of-your-retirement-plan-in-canada"
                element={
                  <Navigate
                    to="/blog/how-life-insurance-can-help-your-family-achieve-long-term-goals"
                    replace
                  />
                }
              />
              <Route
                path="/blog/why-millennials-and-gen-z-should-consider-critical-illness-insurance-early"
                element={
                  <Navigate
                    to="/blog/critical-illness-insurance-everything-you-need-to-know"
                    replace
                  />
                }
              />
              <Route
                path="/blog/build-wealth-in-2026-with-whole-life-insurance-in-canada"
                element={
                  <Navigate
                    to="/blog/how-to-use-whole-life-insurance-in-your-financial-planning"
                    replace
                  />
                }
              />
              <Route
                path="/blog/christmas-gifts-that-last-a-lifetime-buy-children-insurance-this-holiday-season"
                element={<Navigate to="/children-insurance" replace />}
              />
              <Route
                path="/blog/significance-of-disability-insurance"
                element={
                  <Navigate
                    to="/blog/why-disability-insurance-is-essential-protect-your-income-before-its-too-late"
                    replace
                  />
                }
              />
              <Route
                path="/blog/top-6-reasons-why-you-need-life-insurance"
                element={<Navigate to="/life-insurance" replace />}
              />
              <Route
                path="/blog/what-options-do-you-have-when-your-term-life-insurance-is-expiring"
                element={
                  <Navigate
                    to="/blog/my-term-life-insurance-is-expiring-now-what-are-my-options"
                    replace
                  />
                }
              />
              <Route
                path="/blog/answered-what-type-of-life-insurance-may-work-for-you"
                element={
                  <Navigate
                    to="/blog/how-rrsp-in-canada-can-help-you-save-on-taxes"
                    replace
                  />
                }
              />

              {/* Dynamic blog post route must stay after blog redirects */}
              <Route path="/blog/:name" element={<BlogPost />} />

              <Route path="/faqs" element={<Faqs />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsConditions />}
              />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/about-us" element={<AboutUs />} />

              {/* Contact + Team */}
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/our-team" element={<OurTeam />} />
              <Route path="/our-team/aman" element={<Aman />} />
              <Route path="/our-team/sunaina" element={<Sunaina />} />
              <Route path="/our-team/shikha" element={<Shikha />} />
              <Route path="/our-team/gurkiran" element={<Gurkiran />} />
              <Route path="/our-team/yashraj" element={<Yash />} />
              <Route path="/our-team/ekta" element={<Ekta />} />
              <Route path="/our-team/samridhi" element={<Samridhi />} />
              <Route path="/our-team/aarti" element={<Aarti />} />

              <Route path="/author/aman-ahuja" element={<AmanAhuja />} />

              {/* Get Quote pages */}
              <Route path="/quote/vtc" element={<VisitorsInsuranceQuoteFinal />} />
              <Route
                path="/quote/vtc/quote-comparison-tool"
                element={<CompareVtcPage />}
              />
              <Route path="/compare/vtc" element={<ComparePlansPage />} />
              {/* Sign In route */}
              <Route path="/sign-in" element={<SignIn />} />

              {/* Apply Now Route */}
              <Route
                path="/apply/vtc"
                element={<ApplyNowVisitorsInsuranceFinal />}
              />
              <Route path="/apply/life" element={<ApplyNowLifeInsurance />} />
              <Route path="/intake/life" element={<LifeIntake />} />
              <Route path="/admin/life-intake" element={<LifeIntakeAdmin />} />

              {/* Plaza Partner Program landing page */}
              <Route path="/gift-card-meet" element={<GiftCardMeet />} />

              {/* Legacy 404 redirects */}
{/* GSC Soft 404 / 404 fixes */}
<Route path="/home" element={<Navigate to="/" replace />} />

<Route
  path="/get-started"
  element={<Navigate to="/book-a-meeting" replace />}
/>

<Route
  path="/book-appointment"
  element={<Navigate to="/book-a-meeting" replace />}
/>


<Route
  path="/regimes-enregistres-depargne-retraite"
  element={<Navigate to="/" replace />}
/>

              {/*
              <Route
                path="/fr"
                element={<Navigate to="/fr/assurance-vie" replace />}
              />
              */}

              <Route
                path="/our-team/aman-ahuja"
                element={<Navigate to="/our-team/aman" replace />}
              />
              <Route
                path="/our-team/shikha-ahuja"
                element={<Navigate to="/our-team/shikha" replace />}
              />

              <Route
                path="/programme-enregistre-depargne-etudes"
                element={
                  <Navigate to="/registered-education-savings-plan" replace />
                }
              />
              <Route
                path="/assurance-maladies-graves"
                element={<Navigate to="/critical-illness-insurance" replace />}
              />
              {/*
                <Route
                  path="/assurance-vie"
                  element={<Navigate to="/fr/assurance-vie" replace />}
                />
              */}

              <Route
                path="/assurance-invalidite"
                element={<Navigate to="/disability-insurance" replace />}
              />
              <Route
                path="/assurance-super-visa"
                element={<Navigate to="/super-visa-insurance" replace />}
              />
              <Route
                path="/assurance-nouveaux-arrivants"
                element={<Navigate to="/newcomers-insurance" replace />}
              />
              <Route
                path="/compte-depargne-libre-dimpot"
                element={<Navigate to="/tax-free-savings-account" replace />}
              />

              <Route path="/faq" element={<Navigate to="/faqs" replace />} />
              <Route path="/faq-2" element={<Navigate to="/faqs" replace />} />

              <Route
                path="/visitor-to-canada"
                element={<Navigate to="/visitor-to-canada-insurance" replace />}
              />
              <Route
                path="/registered-retirement-savings-plans"
                element={
                  <Navigate to="/registered-retirement-savings-plan" replace />
                }
              />
              <Route
                path="/health-insurance-canada"
                element={<Navigate to="/health-insurance" replace />}
              />

              <Route
                path="/personal-life-insurance"
                element={<Navigate to="/life-insurance" replace />}
              />
              <Route
                path="/mortgage-life-insurance"
                element={<Navigate to="/mortgage-insurance" replace />}
              />
              <Route
                path="/monthly-premium-payments"
                element={<Navigate to="/life-insurance" replace />}
              />

              <Route
                path="/book-appointment"
                element={<Navigate to="/book-a-meeting" replace />}
              />
              <Route
                path="/international-students-financial-roadmap"
                element={
                  <Navigate to="/international-student-insurance" replace />
                }
              />
              <Route
                path="/ei-blog-what-options-do-you-have-when-your-term-life-insurance-is-expiring"
                element={
                  <Navigate
                    to="/blog/my-term-life-insurance-is-expiring-now-what-are-my-options"
                    replace
                  />
                }
              />

              <Route
                path="/how-insurance-protects-you-during-lifes-biggest-milestones"
                element={
                  <Navigate
                    to="/blog/how-insurance-protects-you-during-lifes-biggest-milestones"
                    replace
                  />
                }
              />
              <Route
                path="/blog/111222-4"
                element={
                  <Navigate
                    to="/blog/winter-flu-colds-why-do-you-need-strong-health-insurance-in-canada"
                    replace
                  />
                }
              />
              <Route
                path="/how-contributing-to-a-tfsa-can-boost-your-savings"
                element={
                  <Navigate
                    to="/blog/how-contributing-to-a-tfsa-can-boost-your-savings"
                    replace
                  />
                }
              />
              <Route
                path="/building-your-childs-future-with-resp-plans-how-small-contributions-make-a-big-difference"
                element={
                  <Navigate
                    to="/blog/building-your-childs-future-with-resp-plans-how-small-contributions-make-a-big-difference"
                    replace
                  />
                }
              />
              <Route
                path="/super-visa-insurance-what-you-need-to-know-before-applying"
                element={
                  <Navigate
                    to="/blog/super-visa-insurance-what-you-need-to-know-before-applying"
                    replace
                  />
                }
              />
              <Route
                path="/the-crucial-role-of-critical-illness-insurance-for-canadian-professionals-2"
                element={
                  <Navigate
                    to="/blog/the-crucial-role-of-critical-illness-insurance-for-canadian-professionals"
                    replace
                  />
                }
              />
              <Route
                path="/canada-pauses-parents-grandparents-sponsorship-program-for-2025-explore-other-options-with-einsured-ca"
                element={
                  <Navigate
                    to="/blog/canada-pauses-parents-grandparents-sponsorship-program-for-2025-explore-other-options-with-einsured-ca"
                    replace
                  />
                }
              />
              <Route
                path="/my-term-life-insurance-is-expiring-now-what-are-my-options"
                element={
                  <Navigate
                    to="/blog/my-term-life-insurance-is-expiring-now-what-are-my-options"
                    replace
                  />
                }
              />
              <Route
                path="/your-guide-to-disability-insurance-quotes-in-canada"
                element={
                  <Navigate
                    to="/blog/your-guide-to-disability-insurance-quotes-in-canada"
                    replace
                  />
                }
              />
              <Route
                path="/why-its-essential-to-compare-prices-and-policies-from-different-insurance-providers-before-buying-an-insurance-policy"
                element={
                  <Navigate
                    to="/blog/why-its-essential-to-compare-prices-and-policies-from-different-insurance-providers-before-buying-an-insurance-policy"
                    replace
                  />
                }
              />
              <Route
                path="/choosing-the-right-insurance-based-on-your-occupation"
                element={
                  <Navigate
                    to="/blog/choosing-the-right-insurance-based-on-your-occupation"
                    replace
                  />
                }
              />
              <Route
                path="/return-of-premium-on-critical-illness-insurance-get-covered-or-get-your-money-back"
                element={
                  <Navigate
                    to="/blog/return-of-premium-on-critical-illness-insurance-get-covered-or-get-your-money-back"
                    replace
                  />
                }
              />
              <Route
                path="/why-buy-private-international-medical-insurance-when-studying-in-canada"
                element={
                  <Navigate
                    to="/blog/canada-pauses-parents-grandparents-sponsorship-program-for-2025-explore-other-options-with-einsured-ca"
                    replace
                  />
                }
              />
              <Route
                path="/top-insurance-mistakes-to-avoid-in-canada"
                element={
                  <Navigate
                    to="/blog/top-insurance-mistakes-to-avoid-in-canada"
                    replace
                  />
                }
              />
              <Route
                path="/life-insurance-for-millennials-why-its-time-to-start-now"
                element={
                  <Navigate
                    to="/blog/life-insurance-for-millennials-why-its-time-to-start-now"
                    replace
                  />
                }
              />
              <Route
                path="/how-life-insurance-can-help-your-family-achieve-long-term-goals"
                element={
                  <Navigate
                    to="/blog/how-life-insurance-can-help-your-family-achieve-long-term-goals"
                    replace
                  />
                }
              />
              <Route
                path="/term-insurance"
                element={<Navigate to="/term-life-insurance" replace />}
              />
              <Route
                path="/tax-benefits-of-the-fhsa-how-to-save-on-taxes-while-building-your-down-payment"
                element={
                  <Navigate
                    to="/blog/tax-benefits-of-the-fhsa-how-to-save-on-taxes-while-building-your-down-payment"
                    replace
                  />
                }
              />
              <Route
                path="/how-life-insurance-can-ease-financial-stress-and-build-wealth"
                element={
                  <Navigate
                    to="/blog/how-life-insurance-can-ease-financial-stress-and-build-wealth"
                    replace
                  />
                }
              />
              <Route
                path="/critical-illness-insurance-everything-you-need-to-know"
                element={
                  <Navigate
                    to="/blog/critical-illness-insurance-everything-you-need-to-know"
                    replace
                  />
                }
              />
              <Route
                path="/super-visa-emergency-medical-insurance-the-key-to-a-worry-free-visit-for-your-parents-and-grandparents"
                element={
                  <Navigate
                    to="/blog/super-visa-emergency-medical-insurance-the-key-to-a-worry-free-visit-for-your-parents-and-grandparents"
                    replace
                  />
                }
              />
              <Route
                path="/why-its-crucial-to-follow-government-travel-advisories-insights-for-canadian-travelers"
                element={
                  <Navigate
                    to="/blog/why-its-crucial-to-follow-government-travel-advisories-insights-for-canadian-travelers"
                    replace
                  />
                }
              />
              <Route
                path="/why-disability-insurance-is-essential-protect-your-income-before-its-too-late"
                element={
                  <Navigate
                    to="/blog/why-disability-insurance-is-essential-protect-your-income-before-its-too-late"
                    replace
                  />
                }
              />
              <Route
                path="/answered-what-type-of-life-insurance-may-work-for-you"
                element={
                  <Navigate
                    to="/blog/how-rrsp-in-canada-can-help-you-save-on-taxes"
                    replace
                  />
                }
              />
              <Route
                path="/how-rrsp-can-help-you-save-on-taxes"
                element={
                  <Navigate
                    to="/blog/how-rrsp-in-canada-can-help-you-save-on-taxes"
                    replace
                  />
                }
              />
              <Route
                path="/how-return-of-premium-enhances-critical-illness-coverage"
                element={
                  <Navigate
                    to="/blog/how-return-of-premium-enhances-critical-illness-coverage"
                    replace
                  />
                }
              />
              <Route
                path="/why-women-need-to-prioritize-life-insurance"
                element={
                  <Navigate
                    to="/blog/why-women-need-to-prioritize-life-insurance"
                    replace
                  />
                }
              />
              <Route
                path="/7-common-questions-asked-by-super-visa-applicants"
                element={
                  <Navigate
                    to="/blog/7-common-questions-asked-by-super-visa-applicants"
                    replace
                  />
                }
              />
              <Route
                path="/the-ultimate-guide-to-understanding-benefits-of-universal-life-insurance-in-canada"
                element={
                  <Navigate
                    to="/blog/the-ultimate-guide-to-understanding-benefits-of-universal-life-insurance-in-canada"
                    replace
                  />
                }
              />
              <Route
                path="/affordable-insurance-solutions-for-super-visa-applicants-in-canada"
                element={
                  <Navigate
                    to="/blog/affordable-insurance-solutions-for-super-visa-applicants-in-canada"
                    replace
                  />
                }
              />
              <Route
                path="/permanent-life-insurance-1"
                element={<Navigate to="/permanent-life-insurance" replace />}
              />
              <Route
                path="/how-to-use-whole-life-insurance-in-your-financial-planning"
                element={
                  <Navigate
                    to="/blog/how-to-use-whole-life-insurance-in-your-financial-planning"
                    replace
                  />
                }
              />
              <Route
                path="/life-insurance-for-elderly-in-brampton-a-comprehensive-guide"
                element={
                  <Navigate
                    to="/blog/life-insurance-for-elderly-in-brampton-a-comprehensive-guide"
                    replace
                  />
                }
              />

              {/* ── French routes (/fr/*) ── */}
              <Route path="/fr" element={<HomeFr />} />
              <Route path="/fr/tout-ce-que-nous-vendons" element={<EverythingWeSellFr />} />
              <Route path="/fr/prendre-rendez-vous" element={<GetStartedFr />} />
              <Route path="/fr/rendez-vous/merci" element={<BookAppointmentThankYouFr />} />

              {/* Life (FR) */}
              <Route path="/fr/assurance-vie" element={<LifeInsuranceFr />} />
              <Route path="/fr/assurance-vie-brampton" element={<LifeInsuranceBramptonFr />} />
              <Route path="/fr/assurance-vie-toronto" element={<LifeInsuranceTorontoFr />} />
              <Route path="/fr/assurance-vie-calgary" element={<LifeInsuranceCalgaryFr />} />
              <Route path="/fr/assurance-vie-mississauga" element={<LifeInsuranceMississaugaFr />} />
              <Route path="/fr/assurance-vie-winnipeg" element={<LifeInsuranceWinnipegFr />} />
              <Route path="/fr/guide-assurance-vie" element={<LifeInsuranceEbookFr />} />
              <Route path="/fr/guide-assurance-vie/merci" element={<LifeInsuranceEbookThankYouFr />} />
              <Route path="/fr/assurance-vie-temporaire" element={<TermLifeInsuranceFr />} />
              <Route path="/fr/assurance-vie-permanente" element={<PermanentLifeInsuranceFr />} />
              <Route path="/fr/assurance-hypothecaire" element={<MortgageInsuranceFr />} />
              <Route path="/fr/assurance-vie-entiere" element={<WholeLifeInsuranceFr />} />
              <Route path="/fr/assurance-vie-universelle" element={<UniversalLifeInsuranceFr />} />
              <Route path="/fr/assurance-vie-permanente-participante" element={<ParticipatingPermanentLifeInsuranceFr />} />
              <Route path="/fr/assurance-sans-examen-medical" element={<NoMedicalInsuranceFr />} />
              <Route path="/fr/assurance-enfants" element={<ChildrenInsuranceFr />} />
              <Route path="/fr/assurance-maladie" element={<HealthInsuranceFr />} />

              {/* Travel (FR) */}
              <Route path="/fr/assurance-visiteurs-canada" element={<VisitorToCanadaInsuranceFr />} />
              <Route path="/fr/assurance-super-visa" element={<SuperVisaInsuranceFr />} />
              <Route path="/fr/assurance-nouveaux-arrivants" element={<NewcomersInsuranceFr />} />
              <Route path="/fr/assurance-etudiants-internationaux" element={<InternationalStudentInsuranceFr />} />
              <Route path="/fr/assurance-voyage" element={<TravelInsuranceFr />} />

              {/* Living benefits (FR) */}
              <Route path="/fr/assurance-maladies-graves" element={<CriticalIllnessInsuranceFr />} />
              <Route path="/fr/assurance-invalidite" element={<DisabilityInsuranceFr />} />

              {/* Investments (FR) */}
              <Route path="/fr/compte-epargne-premier-logement" element={<FirstHomeSavingsAccountFr />} />
              <Route path="/fr/regime-epargne-etudes" element={<RegisteredEducationSavingsPlanFr />} />
              <Route path="/fr/compte-epargne-libre-impot" element={<TaxFreeSavingsAccountFr />} />
              <Route path="/fr/regime-epargne-retraite" element={<RegisteredRetirementSavingsPlanFr />} />
              <Route path="/fr/regime-epargne-invalidite" element={<RegisteredDisabilitySavingsPlanFr />} />
              <Route path="/fr/calculateur-reei" element={<EinsuredRdspCalculatorFr />} />

              {/* Resources (FR) */}
              {/* Blog is English-only — redirect the old French blog URLs. */}
              <Route path="/fr/blogue" element={<Navigate to="/blog" replace />} />
              <Route path="/fr/blogue/:name" element={<BlogueToBlogRedirect />} />
              <Route path="/fr/faq" element={<FaqsFr />} />
              <Route path="/fr/avis" element={<ReviewsFr />} />
              <Route path="/fr/etudes-de-cas" element={<CaseStudiesFr />} />
              <Route path="/fr/politique-confidentialite" element={<PrivacyPolicyFr />} />
              <Route path="/fr/conditions-utilisation" element={<TermsConditionsFr />} />
              <Route path="/fr/avertissement" element={<DisclaimerFr />} />
              <Route path="/fr/a-propos" element={<AboutUsFr />} />

              {/* Contact / Team (FR) */}
              <Route path="/fr/contactez-nous" element={<ContactUsFr />} />
              <Route path="/fr/notre-equipe" element={<OurTeamFr />} />
              <Route path="/fr/notre-equipe/aman" element={<AmanFr />} />
              <Route path="/fr/notre-equipe/sunaina" element={<SunainaFr />} />
              <Route path="/fr/notre-equipe/shikha" element={<ShikhaFr />} />
              <Route path="/fr/notre-equipe/gurkiran" element={<GurkiranFr />} />
              <Route path="/fr/notre-equipe/yashraj" element={<YashFr />} />
              <Route path="/fr/notre-equipe/ekta" element={<EktaFr />} />
              <Route path="/fr/notre-equipe/samridhi" element={<SamridhiFr />} />
              <Route path="/fr/notre-equipe/aarti" element={<AartiFr />} />

              {/* Quotes / Apply (FR) */}
              <Route path="/fr/devis/vtc" element={<VisitorsInsuranceQuoteFinalFr />} />
              <Route path="/fr/devis/vtc/comparaison" element={<CompareVtcPageFr />} />
              <Route path="/fr/comparer/vtc" element={<ComparePlansPageFr />} />
              <Route path="/fr/demande/vtc" element={<ApplyNowVisitorsInsuranceFinalFr />} />
              <Route path="/fr/demande/vie" element={<ApplyNowLifeInsuranceFr />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
</Suspense>
          </main>

          <Suspense fallback={null}>
            {showLayout &&
              (isLocationPage ? (
                <LocationFooter />
              ) : isFrench ? (
                <FooterFr removeTopGap={removeFooterGap} />
              ) : (
                <Footer removeTopGap={removeFooterGap} />
              ))}
          </Suspense>

          {showLayout && !isLocationPage && <FloatingScheduleCallButton />}
        </BlogProvider>
      </ReviewsProvider>
    </>
  );
}

export default App;
